export const WEBSITE_NOTIFICATION_EVENT = "instaport:website-notification";

const NOTIFICATION_STORE_KEY = "instaport_website_notified_events_v1";
const NOTIFICATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_ICON = "/assets/logo/favicon.png";
const DEFAULT_URL = "/orders";

const isBrowserEnvironment = () => typeof window !== "undefined";

const isNotificationSupported = () =>
  isBrowserEnvironment() &&
  "Notification" in window &&
  typeof Notification !== "undefined";

const readNotificationStore = () => {
  if (!isBrowserEnvironment()) return {};

  try {
    const rawValue = window.localStorage.getItem(NOTIFICATION_STORE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    const now = Date.now();
    const nextStore = {};

    Object.entries(parsed || {}).forEach(([key, timestamp]) => {
      if (now - Number(timestamp || 0) < NOTIFICATION_TTL_MS) {
        nextStore[key] = Number(timestamp || 0);
      }
    });

    return nextStore;
  } catch (_) {
    return {};
  }
};

const writeNotificationStore = (store) => {
  if (!isBrowserEnvironment()) return;

  try {
    window.localStorage.setItem(
      NOTIFICATION_STORE_KEY,
      JSON.stringify(store || {})
    );
  } catch (_) {}
};

export const hasGrantedNotificationPermission = () =>
  isNotificationSupported() && Notification.permission === "granted";

export const requestWebsiteNotificationPermission = async () => {
  if (!isNotificationSupported()) return "unsupported";

  if (Notification.permission !== "default") {
    return Notification.permission;
  }

  try {
    return await Notification.requestPermission();
  } catch (_) {
    return Notification.permission || "denied";
  }
};

export const wasWebsiteNotificationShown = (eventKey) => {
  if (!eventKey) return false;
  const store = readNotificationStore();
  return Boolean(store[eventKey]);
};

export const markWebsiteNotificationShown = (eventKey) => {
  if (!eventKey) return;
  const store = readNotificationStore();
  store[eventKey] = Date.now();
  writeNotificationStore(store);
};

export const showWebsiteBrowserNotification = ({
  eventKey,
  title,
  body,
  url = DEFAULT_URL,
  tag,
  icon = DEFAULT_ICON,
  requireInteraction = false,
} = {}) => {
  if (!hasGrantedNotificationPermission()) {
    return false;
  }

  if (eventKey && wasWebsiteNotificationShown(eventKey)) {
    return false;
  }

  try {
    const notification = new Notification(title || "Instaport Delivery", {
      body: body || "",
      icon,
      badge: icon,
      tag: tag || eventKey || undefined,
      requireInteraction,
      renotify: false,
      silent: false,
      data: { url },
    });

    notification.onclick = () => {
      try {
        window.focus();
        if (url) {
          window.location.assign(url);
        }
      } catch (_) {}
      notification.close();
    };

    if (eventKey) {
      markWebsiteNotificationShown(eventKey);
    }

    return true;
  } catch (_) {
    return false;
  }
};

export const dispatchWebsiteNotification = (payload = {}) => {
  if (!isBrowserEnvironment()) return;

  window.dispatchEvent(
    new CustomEvent(WEBSITE_NOTIFICATION_EVENT, {
      detail: payload,
    })
  );
};
